import React from "react";
import {
  Autocomplete,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { GROUPS } from "../helpers/evalComparisonApi";

const VersionSelect = ({ id, label, value, versions, exclude, onChange }) => (
  <FormControl size="small" sx={{ minWidth: 200 }}>
    <InputLabel id={`${id}-label`}>{label}</InputLabel>
    <Select labelId={`${id}-label`} label={label} value={value} onChange={(e) => onChange(e.target.value)}>
      {versions
        .filter((v) => v.key !== exclude)
        .map((v) => (
          <MenuItem key={v.key} value={v.key}>
            {v.label}
          </MenuItem>
        ))}
    </Select>
  </FormControl>
);

// Filters for the run comparison. The page owns the view state (in the URL);
// this component shows it and reports changes. `actions` holds extra controls
// such as export (part 5).
// `counts` is { a, b }: group counts for production (a) and test (b).
const ComparisonToolbar = ({ versions, view, scripts, counts, onChange, actions }) => {
  const labelOf = (key) => versions.find((v) => v.key === key)?.label || key;
  const grouped = counts?.[view.by] ?? counts?.a ?? null;
  // "production 9 · test 4", or "9" for a run with one engine version.
  // A count the API does not send shows a dash, never "undefined".
  const n = (value) => value ?? "—";
  const countText = (key) => {
    if (!counts?.a) return "";
    if (!counts.b) return `${n(counts.a[key])}`;
    return `production ${n(counts.a[key])} · test ${n(counts.b[key])}`;
  };

  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ md: "center" }}
        flexWrap="wrap"
        useFlexGap
      >
        <Autocomplete
          size="small"
          options={scripts}
          value={view.script || null}
          onChange={(_, script) => onChange({ script: script || "" })}
          renderInput={(params) => <TextField {...params} label="Script" placeholder="All scripts" />}
          sx={{ minWidth: 260 }}
        />
        {versions.length > 2 && (
          <>
            <VersionSelect
              id="engine-version-production"
              label="Production engine version"
              value={view.a}
              versions={versions}
              exclude={view.b}
              onChange={(a) => onChange({ a })}
            />
            <VersionSelect
              id="engine-version-test"
              label="Test engine version"
              value={view.b}
              versions={versions}
              exclude={view.a}
              onChange={(b) => onChange({ b })}
            />
          </>
        )}
        {view.b && (
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="groups-use-label">Groups use</InputLabel>
            <Select
              labelId="groups-use-label"
              label="Groups use"
              value={view.by}
              onChange={(e) => onChange({ by: e.target.value })}
            >
              <MenuItem value="b">Test: {labelOf(view.b)}</MenuItem>
              <MenuItem value="a">Production: {labelOf(view.a)}</MenuItem>
            </Select>
          </FormControl>
        )}
        {view.b && (
          <FormControlLabel
            control={<Switch checked={view.diff} onChange={(e) => onChange({ diff: e.target.checked })} />}
            label="Only show differences"
          />
        )}
        {actions && <Stack sx={{ ml: { md: "auto" } }}>{actions}</Stack>}
      </Stack>

      <Stack spacing={0.5}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={view.group}
          onChange={(_, group) => group && onChange({ group })}
          aria-label="Outcome group"
          sx={{ flexWrap: "wrap", "& .MuiToggleButton-root": { textTransform: "none" } }}
        >
          <ToggleButton
            value="all"
            aria-label={
              grouped ? `All, ${n(grouped.all)} · ${n(grouped.unlabeled_or_error)} unlabeled or error` : "All"
            }
          >
            <Stack>
              <span>All</span>
              {grouped && (
                <Typography variant="caption">
                  {n(grouped.all)} · {n(grouped.unlabeled_or_error)} unlabeled or error
                </Typography>
              )}
            </Stack>
          </ToggleButton>
          {GROUPS.map((g) => (
            <ToggleButton
              key={g.key}
              value={g.key}
              aria-label={countText(g.key) ? `${g.label}, ${countText(g.key)}` : g.label}
            >
              <Stack>
                <span>{g.label}</span>
                {countText(g.key) && <Typography variant="caption">{countText(g.key)}</Typography>}
              </Stack>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Typography variant="caption" color="text.secondary">
          The table shows the group for {view.by === "b" ? "test" : "production"} (
          {labelOf(view.by === "b" ? view.b : view.a)}). Unlabeled and error rows appear only under All. Brick and False
          positive here count only questions with an expected bucket.
        </Typography>
      </Stack>
    </Stack>
  );
};

export default ComparisonToolbar;
